/**
 * UnitsContext — Imperial ↔ SI preference with AsyncStorage persistence.
 *
 * Usage:
 *   const { isImperial, toggleUnits } = useUnitsContext();
 *   const { unit, toImp, fromImp } = useUnits();
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

const STORAGE_KEY = 'tabai_units_imperial';

// ── Context ──────────────────────────────────────────────────────────────────

interface UnitsContextValue {
  isImperial: boolean;
  toggleUnits: () => void;
  setImperial: (v: boolean) => void;
}

const UnitsContext = createContext<UnitsContextValue>({
  isImperial: true,
  toggleUnits: () => {},
  setImperial: () => {},
});

export const UnitsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isImperial, setIsImperial] = useState(true);

  useEffect(() => {
    AsyncStorage.getItem(STORAGE_KEY).then((v) => {
      if (v !== null) setIsImperial(v === 'true');
    });
  }, []);

  const setImperial = useCallback(async (v: boolean) => {
    setIsImperial(v);
    await AsyncStorage.setItem(STORAGE_KEY, String(v));
  }, []);

  const toggleUnits = useCallback(() => {
    setIsImperial((prev) => {
      const next = !prev;
      AsyncStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  return (
    <UnitsContext.Provider value={{ isImperial, toggleUnits, setImperial }}>
      {children}
    </UnitsContext.Provider>
  );
};

export function useUnitsContext() {
  return useContext(UnitsContext);
}

// ── Unit quantity types ───────────────────────────────────────────────────────

export type UnitQty =
  | 'flow'        // CFM ↔ L/s
  | 'velocity'    // FPM ↔ m/s
  | 'pressure'    // in. w.g. ↔ Pa
  | 'temperature' // °F ↔ °C
  | 'area'        // ft² ↔ m²
  | 'length'      // ft ↔ m
  | 'diameter'    // in ↔ mm
  | 'power'       // HP ↔ kW
  | 'heatRate'    // BTU/hr ↔ W
  | 'density'     // lb/ft³ ↔ kg/m³
  | 'waterFlow'   // GPM ↔ L/min
  | 'head'        // ft (head) ↔ m
  | 'enthalpy'    // BTU/lb ↔ kJ/kg
  | 'humidityRatio' // lb/lb ↔ g/kg (× 1000)
  | 'volume'      // ft³ ↔ m³
  | 'tipSpeed'    // FPM ↔ m/s  (same as velocity)
  | 'percent'     // % (unitless, same both sides)
  | 'rpm'         // RPM (universal)
  | 'ratio'       // dimensionless
  | 'amps'        // A (universal)
  | 'volts'       // V (universal)
  | 'ohms'        // Ω (universal)
  | 'watts';      // W (universal)

// ── Unit label strings ────────────────────────────────────────────────────────

const IMP_LABELS: Record<UnitQty, string> = {
  flow:          'CFM',
  velocity:      'FPM',
  pressure:      'in. w.g.',
  temperature:   '°F',
  area:          'ft²',
  length:        'ft',
  diameter:      'in',
  power:         'HP',
  heatRate:      'BTU/hr',
  density:       'lb/ft³',
  waterFlow:     'GPM',
  head:          'ft',
  enthalpy:      'BTU/lb',
  humidityRatio: 'lb/lb',
  volume:        'ft³',
  tipSpeed:      'FPM',
  percent:       '%',
  rpm:           'RPM',
  ratio:         '',
  amps:          'A',
  volts:         'V',
  ohms:          'Ω',
  watts:         'W',
};

const SI_LABELS: Record<UnitQty, string> = {
  flow:          'L/s',
  velocity:      'm/s',
  pressure:      'Pa',
  temperature:   '°C',
  area:          'm²',
  length:        'm',
  diameter:      'mm',
  power:         'kW',
  heatRate:      'W',
  density:       'kg/m³',
  waterFlow:     'L/min',
  head:          'm',
  enthalpy:      'kJ/kg',
  humidityRatio: 'g/kg',
  volume:        'm³',
  tipSpeed:      'm/s',
  percent:       '%',
  rpm:           'RPM',
  ratio:         '',
  amps:          'A',
  volts:         'V',
  ohms:          'Ω',
  watts:         'W',
};

// ── Conversion factors (SI → Imperial) ───────────────────────────────────────
// Multiply SI input by these to get Imperial

const SI_TO_IMP: Record<UnitQty, (v: number) => number> = {
  flow:          (v) => v / 0.47194745,   // L/s → CFM  (1 CFM = 0.47195 L/s)
  velocity:      (v) => v / 0.00508,      // m/s → FPM
  pressure:      (v) => v / 249.08890,    // Pa → in. w.g.
  temperature:   (v) => v * 9 / 5 + 32,  // °C → °F
  area:          (v) => v / 0.09290304,   // m² → ft²
  length:        (v) => v / 0.3048,       // m → ft
  diameter:      (v) => v / 25.4,         // mm → in
  power:         (v) => v / 0.74569987,   // kW → HP
  heatRate:      (v) => v / 0.29307107,   // W → BTU/hr
  density:       (v) => v / 16.01846,     // kg/m³ → lb/ft³
  waterFlow:     (v) => v / 3.78541,      // L/min → GPM
  head:          (v) => v / 0.3048,       // m → ft
  enthalpy:      (v) => v / 2.326,        // kJ/kg → BTU/lb
  humidityRatio: (v) => v / 1000,         // g/kg → lb/lb
  volume:        (v) => v / 0.02831685,   // m³ → ft³
  tipSpeed:      (v) => v / 0.00508,      // m/s → FPM (same as velocity)
  // Universal quantities — no conversion
  percent:       (v) => v,
  rpm:           (v) => v,
  ratio:         (v) => v,
  amps:          (v) => v,
  volts:         (v) => v,
  ohms:          (v) => v,
  watts:         (v) => v,
};

// Multiply Imperial result by these to get SI display value

const IMP_TO_SI: Record<UnitQty, (v: number) => number> = {
  flow:          (v) => v * 0.47194745,
  velocity:      (v) => v * 0.00508,
  pressure:      (v) => v * 249.08890,
  temperature:   (v) => (v - 32) * 5 / 9,
  area:          (v) => v * 0.09290304,
  length:        (v) => v * 0.3048,
  diameter:      (v) => v * 25.4,
  power:         (v) => v * 0.74569987,
  heatRate:      (v) => v * 0.29307107,
  density:       (v) => v * 16.01846,
  waterFlow:     (v) => v * 3.78541,
  head:          (v) => v * 0.3048,
  enthalpy:      (v) => v * 2.326,
  humidityRatio: (v) => v * 1000,
  volume:        (v) => v * 0.02831685,
  tipSpeed:      (v) => v * 0.00508,
  percent:       (v) => v,
  rpm:           (v) => v,
  ratio:         (v) => v,
  amps:          (v) => v,
  volts:         (v) => v,
  ohms:          (v) => v,
  watts:         (v) => v,
};

// ── useUnits hook ─────────────────────────────────────────────────────────────

/**
 * Returns helpers for unit-aware calc screens.
 *
 * toImp(strValue, qty)  — parse a displayed string and convert to Imperial for math.
 *                         Returns NaN if the string is empty/invalid.
 * fromImp(impNum, qty)  — convert an Imperial number to a display string in current units.
 * unit(qty)             — unit label string ('CFM' or 'L/s' etc.)
 * isImperial            — the raw boolean
 */
export function useUnits() {
  const { isImperial } = useUnitsContext();

  const unit = (qty: UnitQty): string =>
    isImperial ? IMP_LABELS[qty] : SI_LABELS[qty];

  /** Parse display string → Imperial number for formula. */
  const toImp = (strValue: string, qty: UnitQty): number => {
    const n = parseFloat(strValue);
    if (!Number.isFinite(n)) return NaN;
    return isImperial ? n : SI_TO_IMP[qty](n);
  };

  /** Convert Imperial number → display string in current units. */
  const fromImp = (impValue: number, qty: UnitQty, decimals = 4): string => {
    if (!Number.isFinite(impValue)) return '';
    const displayed = isImperial ? impValue : IMP_TO_SI[qty](impValue);
    // Trim trailing zeros but keep at least 2 decimal places for readability
    return parseFloat(displayed.toFixed(decimals)).toString();
  };

  return { isImperial, unit, toImp, fromImp };
}
